import { useState, useEffect } from "react"
import { Button, Card, Empty, Flex, Form, Input, Space, Spin } from "antd"
import { PlusOutlined, LoadingOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Modal } from "ui/Modal"
import CustomDrawer from "ui/Drawer"
import { Text } from "ui/Typography"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { colors, spacings } from "themes/variables"
import PaymentMethodList from "pagesV2/pro/Bookings/BookingForm/PaymentMethodList"
import AddCardModal from "pagesV2/pro/Bookings/BookingForm/AddCardModal"
import NoCardImage from "ui/Icon/DSIcon/NoCardImage"
import StripeWrapper from "components/stripe/StripeWrapper"
import {
  attachUserPaymentMethod,
  cleanedPaymentMethods,
  getPaymentMethods,
  paymentMethodsLoadingSelector,
  paymentMethodsSelector,
  setSelectedPaymentMethodId
} from "store/user"
import { setClientId } from "store/bookings"
import { notification } from "ui/Notification"
import { receiveErrors, resetErrors } from "modules/errors/reducer"
import { useStripe, useElements, CardNumberElement } from "@stripe/react-stripe-js"

const PlaceHoldForm = ({ onClose, onSubmit, clientId }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const stripe = useStripe()
  const elements = useElements()
  const [modalForm] = Form.useForm()

  const paymentMethods = useSelector(paymentMethodsSelector)
  const paymentMethodsLoading = useSelector(paymentMethodsLoadingSelector)
  const selectedPaymentMethodId = useSelector((state) => state.user.selectedPaymentMethodId)
  const isPlacingHold = useSelector((state) => state.bookings.cardHold.loading)

  const [selectedCardId, setSelectedCardId] = useState(null)
  const [cardError, setCardError] = useState("")
  const [amount, setAmount] = useState("")
  const [amountError, setAmountError] = useState("")
  const [isAddCardVisible, setIsAddCardVisible] = useState(false)
  const [isAddingCard, setIsAddingCard] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (clientId) {
      dispatch(cleanedPaymentMethods())
      dispatch(getPaymentMethods(clientId))
    }
  }, [clientId, dispatch])

  useEffect(() => {
    if (paymentMethods?.length && !selectedCardId) {
      setSelectedCardId(paymentMethods[0].payment_method_id)
    }
  }, [paymentMethods, selectedCardId])

  const handleAmountChange = (e) => {
    const val = e.target.value
    setAmount(val)
    setAmountError(Number(val) > 0 ? "" : t("client.payment.card_hold.place_hold_modal.invalid_amount_error"))
  }

  const handleCancelAddCard = () => {
    modalForm.resetFields()
    setIsAddCardVisible(false)
  }

  const createPaymentMethod = async () => {
    setIsAddingCard(true)
    try {
      const formData = await modalForm.validateFields()

      if (!stripe || !elements) return

      const cardNumberElement = elements.getElement(CardNumberElement)
      if (!cardNumberElement) return

      dispatch(resetErrors())

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
        billing_details: {
          name: formData.cardHolderName,
          address: { postal_code: formData.zip }
        }
      })

      if (error) {
        if (error.type === "card_error") {
          dispatch(receiveErrors({ data: { message: { card_error: error.message } }, showToast: false }))
        } else {
          notification.error({ message: t("client.payment.card_hold.place_hold_modal.stripe_error") })
        }
        return
      }

      const isNewUser = !clientId || clientId === "null" || clientId === "undefined"
      const payload = {
        new_user: isNewUser,
        id: clientId,
        payment_method_id: paymentMethod.id,
        guide_id: false,
        client_attributes: {}
      }

      const res = dispatch(attachUserPaymentMethod(payload))
      const newPaymentMethodId = res?.payment_methods?.[0]?.payment_method_id
      const userId = res?.user_id || clientId

      dispatch(setClientId(userId))
      dispatch(getPaymentMethods(userId))
      dispatch(setSelectedPaymentMethodId(newPaymentMethodId))
      setSelectedCardId(newPaymentMethodId)
      setCardError("")

      if (elements._elements) {
        for (const el of elements._elements) {
          el.clear?.()
        }
      }

      notification.success({ message: t("client.payment.card_hold.place_hold_modal.card_added_success") })
      handleCancelAddCard()
    } catch {
      // form validation errors are handled by antd
    } finally {
      setIsAddingCard(false)
    }
  }

  const handleSubmit = async () => {
    if (!selectedCardId) {
      setCardError(t("client.payment.card_hold.place_hold_modal.select_payment_method_error"))
      return
    }
    if (!amount || Number(amount) <= 0) {
      setAmountError(t("client.payment.card_hold.place_hold_modal.invalid_amount_error"))
      return
    }
    setIsSubmitting(true)
    try {
      const selected = paymentMethods?.find((m) => m.payment_method_id === selectedCardId)
      await onSubmit({
        payment_method_id: selectedCardId,
        amount: Number(amount),
        card_brand: selected?.brand,
        last4: selected?.last4
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Spin spinning={isSubmitting} indicator={<LoadingOutlined spin />} size="large">
      <Space direction="vertical" size={24} className="w-full">
        <Spin spinning={paymentMethodsLoading}>
          <Space direction="vertical" size={8} className="w-full">
            <Text fw={500}>{t("client.payment.card_hold.place_hold_modal.payment_method")}</Text>
            {paymentMethods?.length ? (
              <>
                <PaymentMethodList
                  active={selectedCardId ?? selectedPaymentMethodId}
                  setActive={(id) => { setSelectedCardId(id); setCardError("") }}
                  paymentMethods={paymentMethods}
                  clientId={clientId}
                />
                <Button
                  color="primary"
                  variant="link"
                  icon={<PlusOutlined />}
                  style={{ fontWeight: 500, padding: 0 }}
                  onClick={() => setIsAddCardVisible(true)}
                >
                  {t("client.payment.card_hold.place_hold_modal.add_card")}
                </Button>
              </>
            ) : (
              !paymentMethodsLoading && (
                <Card>
                  <Empty
                    image={<NoCardImage />}
                    description={
                      <>
                        <p>{t("client.payment.card_hold.place_hold_modal.no_saved_methods")}</p>
                        <Button icon={<PlusOutlined />} type="primary" onClick={() => setIsAddCardVisible(true)}>
                          {t("client.payment.card_hold.place_hold_modal.add_card")}
                        </Button>
                      </>
                    }
                  />
                </Card>
              )
            )}
            {cardError && <Text color={colors.error}>{cardError}</Text>}
          </Space>
        </Spin>

        <Flex vertical gap={spacings.XXS}>
          <Text>{t("client.payment.card_hold.place_hold_modal.hold_amount")}</Text>
          <Input
            prefix="$"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={handleAmountChange}
            status={amountError ? "error" : ""}
            placeholder={t("client.payment.card_hold.place_hold_modal.amount_placeholder")}
          />
          {amountError && <Text color={colors.error}>{amountError}</Text>}
        </Flex>

        <Flex gap={spacings.S}>
          <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
            {t("client.payment.card_hold.place_hold_modal.cancel")}
          </Button>
          <Button
            type="primary"
            className="py-l px-xl"
            disabled={!selectedCardId || !amount || !!amountError || isPlacingHold}
            loading={isPlacingHold}
            onClick={handleSubmit}
          >
            {t("client.payment.card_hold.place_hold_modal.submit")}
          </Button>
        </Flex>
      </Space>

      {isAddCardVisible && (
        <AddCardModal
          visible={isAddCardVisible}
          onCancel={handleCancelAddCard}
          onSubmit={createPaymentMethod}
          form={modalForm}
          loading={isAddingCard}
        />
      )}
    </Spin>
  )
}

const PlaceHoldModal = ({ open, onClose, onSubmit, clientId, bookingUUID }) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm", "md"].includes(breakpoint)

  const content = (
    <StripeWrapper>
      <PlaceHoldForm onClose={onClose} onSubmit={onSubmit} clientId={clientId} bookingUUID={bookingUUID} />
    </StripeWrapper>
  )

  return isMobile ? (
    <CustomDrawer open={open} setOpen={onClose} title={t("client.payment.card_hold.place_hold_modal.title")}>
      {content}
    </CustomDrawer>
  ) : (
    <Modal centered title={t("client.payment.card_hold.place_hold_modal.title")} open={open} onCancel={onClose} width={700} maskClosable={false}>
      {content}
    </Modal>
  )
}

PlaceHoldForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bookingUUID: PropTypes.string
}

PlaceHoldModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bookingUUID: PropTypes.string
}

export default PlaceHoldModal
